import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    AutoAwesome,
    Cancel as CancelIcon,
    CancelOutlined,
    CheckCircleOutline,
    ContentCopy as CopyIcon,
    TrendingUp,
} from '@mui/icons-material';
import {useReportStore} from '../../../useReportStore';
import {useReportAccess} from '../../../reportPage/useReportAccess';
import {useSession} from '../../../../../firebase/UserProvider';
import {subscribeToExperts} from '../../../../../firebase/firestore/experts';
import {createBlockNode, createHeading} from '../../../../../components/prosemirror-editor/helpers/nodeCreation';
import UserAvatar from '../../../../../components/UserAvatar';
import {generateUUID} from '../../../../../components/prosemirror-editor/helpers/utils';
import {addReference, initializeReferenceMark} from '../../../../../components/prosemirror-editor/yMaps/referencesYMap';
import {setActiveMarginMarker} from '../../../../../components/prosemirror-editor/plugins/marginMarkersPlugin';
import useYDocStore from '../../../../../stores/useYDocStore';
import {buildForecastSourceIndex, OpinionEvidenceText, PersonaReferencesList} from './ForecastCitations';
import {ForecastSourcesList} from './ForecastSourcesList';
import {buildForecastReferenceEntries} from './forecastReferenceInsertion';
import {extractForecastSectionText} from './forecastSectionExtraction';
import {ForecastPreviewText} from './ForecastPreview';
import {useForecastJob} from './useForecastJob';
import ForecastHistory from './ForecastHistory';
import {suggestPersonas} from '../../../../../utils/api/aiCitationAnalysis';
import {listReportVersionsFromApi} from '../../../../../utils/api/reportVersions';

// AI-suggested Forecast personas: personas scoring at/above this suitability threshold are
// auto-checked after a successful "Suggest personas" call (unless the user already manually
// (un)checked them since the panel opened — see `manuallyToggledRef` in the component below).
const PERSONA_SUGGESTION_AUTO_CHECK_THRESHOLD = 70;

const ROLE_LABELS = {
    analyst: 'Analyst',
    expert: 'Expert',
    reviewer: 'Reviewer',
    real_user: 'User',
    admin: 'Admin',
};

function roleLabel(option) {
    // Always surface "Expert" if the user carries expert in any role slot
    if (option.role === 'expert' || (Array.isArray(option.roles) && option.roles.includes('expert'))) {
        return 'Expert';
    }
    const raw = option.role || (option.roles && option.roles[0]);
    if (!raw) return null;
    return ROLE_LABELS[raw] || raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const HORIZON_OPTIONS = [
    {value: '1_MONTH', label: '1 month'},
    {value: '3_MONTHS', label: '3 months'},
    {value: '6_MONTHS', label: '6 months'},
    {value: '1_YEAR', label: '1 year'},
];

const CREATIVITY_MARKS = [
    {value: 0, label: 'Cautious'},
    {value: 1, label: 'Conservative'},
    {value: 2, label: 'Exploratory'},
    {value: 3, label: 'Speculative'},
];

const FORECAST_HEADING_TEXT = 'FORECAST';

// Human-readable explanations for the Generate button's live phase label, so a long-running
// generation reads as "working" rather than "stuck" — keyed by the exact phase strings the
// backend emits (see ForecastService's PHASE_* constants). Kept on the frontend since these are
// purely explanatory captions, not part of the generation contract itself.
const PHASE_DESCRIPTIONS = {
    'Reading your report...': 'Parsing your report so the forecast is grounded in its actual content.',
    'Gathering source documents...': 'Collecting related source documents for supporting evidence.',
    'Planning research queries...': 'Deciding what additional evidence to search for.',
    'Searching for supporting evidence...': 'Searching the knowledge base for evidence to ground the forecast.',
    'Generating forecast...': 'Running the forecast model. This step can take a few minutes for complex '
        + "reports — it's safe to navigate away; the job keeps running and will be here when you return.",
};
const PERSONA_OPINION_PHASE_REGEX = /^Getting .+'s opinion\.\.\.$/;

/** Explanatory caption for the current live phase, or null if there's nothing extra to say. */
function describeForecastPhase(phase) {
    if (!phase) return null;
    if (PHASE_DESCRIPTIONS[phase]) return PHASE_DESCRIPTIONS[phase];
    if (PERSONA_OPINION_PHASE_REGEX.test(phase)) {
        return "Drafting this persona's opinion based on their expertise and the forecast so far.";
    }
    return null;
}

/** Formats a whole-second duration as "1m 24s" (or "45s" under a minute). */
function formatElapsedDuration(totalSeconds) {
    if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return null;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}

/**
 * Forecast component for AI Assistant
 * Generates a forward-looking forecast (with optional persona opinions) for the report
 * and displays it in the panel.
 */
const Forecast = () => {
    const {reportId} = useParams();
    const editorView = useReportStore(s => s.editorView);
    const {user} = useSession() || {};
    const {role} = useReportAccess(reportId, user);
    const isOwner = role === 'owner';

    const [activeTab, setActiveTab] = useState('generate');
    const [instruction, setInstruction] = useState('');
    const [horizon, setHorizon] = useState('3_MONTHS');
    const [creativity, setCreativity] = useState(2);
    const [selectedPersonas, setSelectedPersonas] = useState([]);
    const [availablePersonas, setAvailablePersonas] = useState([]);
    // AI-suggested Forecast personas: null before the first successful "Suggest personas" call,
    // then a handle -> {suitabilityScore, reason} map for every ranked candidate.
    const [personaSuggestions, setPersonaSuggestions] = useState(null);
    const [suggestingPersonas, setSuggestingPersonas] = useState(false);
    const [suggestError, setSuggestError] = useState(null);
    // Handles the user has explicitly (un)checked since the panel opened — re-running "Suggest
    // personas" must never override an explicit user choice, even if the new score qualifies.
    const manuallyToggledRef = useRef(new Set());
    // Guard: auto-suggest fires at most once per component lifecycle (on first persona load).
    const autoSuggestedRef = useRef(false);
    const [copiedKey, setCopiedKey] = useState(null);
    const [localError, setLocalError] = useState(null);

    const {
        currentPhase,
        forecast,
        error: jobError,
        streamUnavailable,
        isActive: isGenerating,
        requestContext,
        contextLoaded,
        startedAt,
        startGenerate,
        cancel,
    } = useForecastJob(reportId);

    const error = localError || jobError;

    const {sourcesByKey, numberByKey} = useMemo(() => buildForecastSourceIndex(forecast), [forecast]);

    /**
     * Button label: static "Generate Forecast" while idle, the live current phase while a job
     * is queued/running (falls back to a static "Generating..." if the phase stream couldn't be
     * established at all — the job still completes server-side either way), per ticket 02.
     */
    const generateButtonLabel = useMemo(() => {
        if (!isGenerating) {
            return 'Generate Forecast';
        }
        if (streamUnavailable || !currentPhase) {
            return 'Generating...';
        }
        return currentPhase;
    }, [isGenerating, streamUnavailable, currentPhase]);

    // Live elapsed-time counter for the in-flight job, ticking once a second so a long-running
    // phase (e.g. the actual LLM call) visibly keeps counting up instead of looking frozen/stuck.
    // Recomputed from `startedAt` (server-authoritative when restored after a refresh) rather than
    // counted from mount, so a page refresh mid-generation doesn't reset the clock back to 0.
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    useEffect(() => {
        if (!isGenerating || !startedAt) {
            setElapsedSeconds(0);
            return undefined;
        }
        const tick = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
        tick();
        const intervalId = setInterval(tick, 1000);
        return () => clearInterval(intervalId);
    }, [isGenerating, startedAt]);

    const generateStatusExplanation = useMemo(() => {
        if (!isGenerating) return null;
        const description = streamUnavailable
            ? 'Your forecast is still generating on the server — this panel just can\'t show live '
              + "step-by-step progress right now. It'll appear here once it's done."
            : describeForecastPhase(currentPhase);
        const elapsed = formatElapsedDuration(elapsedSeconds);
        if (!description && !elapsed) return null;
        if (!elapsed) return description;
        return description ? `${description} (${elapsed} elapsed)` : `${elapsed} elapsed`;
    }, [isGenerating, streamUnavailable, currentPhase, elapsedSeconds]);

    // Subscribe to available Experts/Personas
    useEffect(() => {
        return subscribeToExperts((experts) => {
            setAvailablePersonas(Array.isArray(experts) ? experts : []);
        });
    }, []);

    // Restore-on-reload: once the latest job's requestContext arrives, re-populate the Generate
    // tab's own inputs (question/horizon/analysis depth/persona suitability suggestions) so a
    // page refresh doesn't reset them to defaults. Guarded by a ref so this only ever runs once
    // per mount — it must never clobber the user's own edits made afterward.
    const restoredContextRef = useRef(false);
    useEffect(() => {
        if (restoredContextRef.current || !requestContext) return;
        restoredContextRef.current = true;

        if (requestContext.userInstructions) setInstruction(requestContext.userInstructions);
        if (requestContext.horizon) setHorizon(requestContext.horizon);
        if (typeof requestContext.creativity === 'number') setCreativity(requestContext.creativity);

        if (Array.isArray(requestContext.personaSuggestions) && requestContext.personaSuggestions.length > 0) {
            const byHandle = {};
            requestContext.personaSuggestions.forEach((s) => {
                if (s?.handle) byHandle[s.handle] = s;
            });
            setPersonaSuggestions(byHandle);
        }
    }, [requestContext]);

    // Restore-on-reload (persona checkboxes): `personaHandles` on requestContext are just
    // strings — resolve them to full persona objects from the live experts roster once it has
    // loaded, so the checkboxes reflect the same selection the job was generated with. Runs once,
    // gated on `availablePersonas` being populated (unless there's nothing to resolve).
    const restoredSelectionRef = useRef(false);
    useEffect(() => {
        if (restoredSelectionRef.current || !requestContext) return;
        const handles = Array.isArray(requestContext.personaHandles) ? requestContext.personaHandles : [];
        if (handles.length === 0) {
            restoredSelectionRef.current = true;
            return;
        }
        if (availablePersonas.length === 0) return;

        restoredSelectionRef.current = true;
        const byHandle = new Map(availablePersonas.map((p) => [p.handle, p]));
        const resolved = handles.map((handle) => byHandle.get(handle)).filter(Boolean);
        if (resolved.length > 0) setSelectedPersonas(resolved);
    }, [requestContext, availablePersonas]);

    /**
     * Get document content from editor
     */
    const getDocumentContent = useCallback(() => {
        if (!editorView) return null;

        if (editorView.editor) {
            return editorView.editor.getJSON();
        }

        return editorView.state?.doc?.toJSON();
    }, [editorView]);

    /**
     * Handle generate button click: starts a durable forecast job and subscribes to its
     * live phase-event stream (MINSKY-16818 ticket 02). The button label tracks the job's
     * current phase via {@code useForecastJob}; if the phase stream can't be established at
     * all, the job still runs server-side and the button shows a static "Generating..." label.
     */
    const handleGenerate = useCallback(async () => {
        if (!reportId || isGenerating) return;

        const content = getDocumentContent();
        if (!content) {
            setLocalError('Editor not available or could not get document content');
            return;
        }
        setLocalError(null);

        try {
            const personaHandles = selectedPersonas.map(p => p.handle);
            const existingForecastText = extractForecastSectionText(content) || undefined;
            console.log('🔮 Starting forecast job with:', {instruction, horizon, creativity, personaHandles});

            // Fetch the current latest report version so the job records which document
            // snapshot it was produced from. Best-effort: null is fine if unavailable.
            let reportVersionNumber = null;
            try {
                const versions = await listReportVersionsFromApi(reportId);
                if (versions.length > 0) {
                    reportVersionNumber = versions[0].version;
                }
            } catch (vErr) {
                console.warn('[Forecast] could not fetch latest report version:', vErr);
            }

            await startGenerate(content, {
                userInstructions: instruction || undefined,
                horizon,
                creativity,
                personaHandles,
                existingForecastText,
                // Restore-on-reload metadata only (see useForecastJob's `requestContext`) — the
                // suitability suggestions currently showing, if any, get persisted with the job
                // so a page refresh can re-show the same chips/reasons without re-suggesting.
                personaSuggestions: personaSuggestions ? Object.values(personaSuggestions) : undefined,
                reportVersionNumber,
            });
        } catch (err) {
            console.error('❌ Error starting forecast job:', err);
        }
    }, [reportId, isGenerating, instruction, horizon, creativity, selectedPersonas, personaSuggestions, getDocumentContent, startGenerate]);

    /**
     * MINSKY-16818 (ticket 03): cancel the in-progress forecast job.
     */
    const handleCancel = useCallback(async () => {
        await cancel();
    }, [cancel]);

    /**
     * Copy plain-text representation of the whole forecast (or a persona opinion) to clipboard
     */
    const handleCopyText = useCallback((text, key) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopiedKey(key);
                setTimeout(() => setCopiedKey(null), 2000);
            })
            .catch(err => {
                console.error('❌ Failed to copy text:', err);
            });
    }, []);

    const formatForecastAsText = useCallback((data) => {
        if (!data) return '';
        const lines = [];
        if (data.summary) lines.push(data.summary, '');
        if (data.keyDrivers?.length) {
            lines.push('Key drivers:');
            data.keyDrivers.forEach(driver => lines.push(`- ${driver}`));
            lines.push('');
        }
        if (data.predictions?.length) {
            lines.push('Predictions:');
            data.predictions.forEach((pred, idx) => {
                lines.push(`${idx + 1}. ${pred.statement} (${pred.confidence} confidence, ${pred.basis})`);
                if (pred.rationale) lines.push(`   Rationale: ${pred.rationale}`);
            });
        }
        return lines.join('\n');
    }, []);

    /**
     * Find or create the "FORECAST" heading at the top level and return
     * where new content should be inserted, mirroring AlternativeHypotheses' approach.
     */
    const findForecastInsertPoint = useCallback((doc) => {
        let forecastPos = null;
        let forecastLevel = null;
        let insertPos = null;

        doc.descendants((node, pos, parent) => {
            if (parent !== doc) return true;

            if (node.type.name === 'heading' && node.textContent === FORECAST_HEADING_TEXT) {
                forecastPos = pos;
                forecastLevel = node.attrs.level;
                return true;
            }

            if (forecastPos !== null && insertPos === null) {
                if (node.type.name === 'heading' && node.attrs.level <= forecastLevel) {
                    insertPos = pos;
                    return false;
                }
            }

            return true;
        });

        if (forecastPos !== null && insertPos === null) {
            insertPos = doc.content.size;
        }

        return {forecastPos, forecastLevel, insertPos};
    }, []);

    /**
     * Insert the full forecast (summary, key drivers, predictions, persona opinions)
     * under a "## FORECAST" heading, tagging inserted content with the AI author mark.
     */
    const handleAddToDocument = useCallback(() => {
        if (!editorView || !forecast) {
            console.error('No editor view or forecast available');
            return;
        }

        const {state, dispatch} = editorView;
        const {schema, doc} = state;
        const authorMark = schema.marks.author;
        const referenceMarkType = schema.marks.reference;
        const strongMarkType = schema.marks.strong;

        if (!authorMark) {
            console.error('Author mark type not found in schema');
            return;
        }
        if (!referenceMarkType) {
            console.error('Reference mark type not found in schema');
            return;
        }
        if (!strongMarkType) {
            console.error('Strong mark type not found in schema');
            return;
        }

        const aiAuthorMark = authorMark.create({userId: 'ai'});
        const aiStrongMark = strongMarkType.create();
        const textNode = (text) => schema.text(text, [aiAuthorMark]);
        // MINSKY-16818: bold text node used only for a prediction's label/statement — headings,
        // the summary, key drivers, and the rationale line keep using the plain `textNode` above.
        const boldTextNode = (text) => schema.text(text, [aiAuthorMark, aiStrongMark]);

        /**
         * MINSKY-16818 (ticket 01): a prediction's cited sources become ONE standard `reference`
         * mark (one uid) wrapping the prediction's own label text — mirroring how
         * `applyResolvedReferences`/`applyStatementReferenceMark` mark real statement text and
         * let `referenceCitationPlugin`'s decorations render the "[n,m]" number, rather than
         * baking `citationRef`-style "[n]" text into the doc. All of a position's cited sources
         * attach as multiple `addedReferences` entries under that single uid (queued here,
         * written to Yjs after dispatch below).
         */
        const pendingReferenceWrites = [];
        let firstReferenceUid = null;

        const buildPredictionLabelNode = (label, citedSources) => {
            const entries = buildForecastReferenceEntries(citedSources, sourcesByKey);
            if (!entries.length) {
                return boldTextNode(label);
            }

            const uid = generateUUID();
            if (!firstReferenceUid) firstReferenceUid = uid;
            pendingReferenceWrites.push({uid, entries});

            return schema.text(label, [aiAuthorMark, aiStrongMark, referenceMarkType.create({uid})]);
        };

        let {forecastPos, forecastLevel, insertPos} = findForecastInsertPoint(doc);

        let tr = state.tr;
        const nodesToInsert = [];
        let sectionHeadingNode = null;

        if (forecastPos === null) {
            sectionHeadingNode = createHeading(schema, 2, null, textNode(FORECAST_HEADING_TEXT));
            forecastLevel = 2;
            insertPos = doc.content.size;
            nodesToInsert.push(sectionHeadingNode);
        }

        const subLevel = forecastLevel + 1;

        if (forecast.summary) {
            nodesToInsert.push(createHeading(schema, subLevel, null, textNode('Summary')));
            nodesToInsert.push(createBlockNode(schema.nodes.paragraph, null, textNode(forecast.summary)));
        }

        if (forecast.keyDrivers?.length) {
            nodesToInsert.push(createHeading(schema, subLevel, null, textNode('Key Drivers')));
            forecast.keyDrivers.forEach(driver => {
                nodesToInsert.push(createBlockNode(schema.nodes.paragraph, null, textNode(`• ${driver}`)));
            });
        }

        if (forecast.predictions?.length) {
            nodesToInsert.push(createHeading(schema, subLevel, null, textNode('Predictions')));
            forecast.predictions.forEach((pred, idx) => {
                const label = `${idx + 1}. ${pred.statement} (${pred.confidence} confidence, ${pred.basis})`;
                const labelNode = buildPredictionLabelNode(label, pred.citedSources);
                nodesToInsert.push(createBlockNode(schema.nodes.paragraph, null, labelNode));
                if (pred.rationale) {
                    nodesToInsert.push(createBlockNode(schema.nodes.paragraph, null, textNode(pred.rationale)));
                }
            });
        }

        if (nodesToInsert.length === 0) {
            return;
        }

        tr = tr.insert(insertPos, nodesToInsert);
        const scrollToPos = sectionHeadingNode ? insertPos + sectionHeadingNode.nodeSize : insertPos;

        tr = tr.setMeta('skipAuthorPlugin', true);
        tr = tr.setSelection(state.selection.constructor.near(tr.doc.resolve(scrollToPos)));

        dispatch(tr);
        console.log('✅ Forecast transaction dispatched, document updated');

        if (pendingReferenceWrites.length) {
            const {ydoc, yReferenceMarks} = useYDocStore.getState();
            if (ydoc && yReferenceMarks) {
                ydoc.transact(() => {
                    pendingReferenceWrites.forEach(({uid, entries}) => {
                        initializeReferenceMark(ydoc, yReferenceMarks, uid);
                        entries.forEach((entry) => addReference(ydoc, yReferenceMarks, uid, entry));
                    });
                }, null);
            } else {
                console.warn('[Forecast] Y.Doc not ready — forecast citations not written to yReferenceMarks');
            }

            // Bulk-inserting a whole forecast can create many citations at once — only the
            // first inserted citation should auto-activate/open the margin rail.
            if (firstReferenceUid) {
                setActiveMarginMarker(editorView, {type: 'reference', uid: firstReferenceUid});
            }
        }

        setTimeout(() => {
            try {
                const currentState = editorView.state;
                const selectionPos = currentState.selection.from;
                const domAtPos = editorView.domAtPos(selectionPos);

                if (domAtPos && domAtPos.node) {
                    let targetElement = domAtPos.node.nodeType === Node.ELEMENT_NODE
                        ? domAtPos.node
                        : domAtPos.node.parentElement;

                    while (targetElement && !targetElement.dataset?.blockId &&
                    !['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P'].includes(targetElement.tagName)) {
                        targetElement = targetElement.parentElement;
                    }

                    if (targetElement) {
                        targetElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                            inline: 'nearest'
                        });
                    }
                }
            } catch (err) {
                console.warn('Could not scroll to position:', err);
            }
            editorView.focus();
        }, 100);
    }, [editorView, forecast, findForecastInsertPoint, sourcesByKey]);

    const personaOptions = useMemo(
        () => availablePersonas.filter(p => p.handle && (
            p.role === 'expert' || (Array.isArray(p.roles) && p.roles.includes('expert'))
        )),
        [availablePersonas]
    );

    /**
     * AI-suggested Forecast personas: ranks every persona option by suitability against the
     * current report content + instructions, then auto-checks every persona scoring at/above
     * {@link PERSONA_SUGGESTION_AUTO_CHECK_THRESHOLD} that the user hasn't already manually
     * (un)checked since the panel opened.
     */
    const handleSuggestPersonas = useCallback(async () => {
        if (!reportId || suggestingPersonas) return;

        const content = getDocumentContent();
        if (!content) {
            setSuggestError('Editor not available or could not get document content');
            return;
        }

        setSuggestError(null);
        setSuggestingPersonas(true);
        try {
            const suggestions = await suggestPersonas(reportId, content, instruction || undefined);
            const byHandle = {};
            (Array.isArray(suggestions) ? suggestions : []).forEach((s) => {
                if (s?.handle) byHandle[s.handle] = s;
            });
            setPersonaSuggestions(byHandle);

            setSelectedPersonas((prev) => {
                const alreadySelectedHandles = new Set(prev.map((p) => p.handle));
                const additions = personaOptions.filter((option) => {
                    const suggestion = byHandle[option.handle];
                    return suggestion
                        && suggestion.suitabilityScore >= PERSONA_SUGGESTION_AUTO_CHECK_THRESHOLD
                        && !alreadySelectedHandles.has(option.handle)
                        && !manuallyToggledRef.current.has(option.handle);
                });
                return additions.length ? [...prev, ...additions] : prev;
            });
        } catch (err) {
            console.error('❌ Error suggesting forecast personas:', err);
            setSuggestError(err?.message || 'Failed to suggest personas');
        } finally {
            setSuggestingPersonas(false);
        }
    }, [reportId, suggestingPersonas, getDocumentContent, instruction, personaOptions]);

    // Auto-suggest once on mount: when the persona roster has loaded, the editor is ready, the
    // mount-time restore-from-requestContext fetch has settled (so we know whether there's a
    // saved requestContext to restore from), and there are still no suggestions to show (neither
    // restored nor from a prior "Suggest" click). This covers the "context is empty" case where
    // the user never typed a question — they still see ranked personas with reasons without
    // clicking the button.
    //
    // Guards against a same-render race with the requestContext-restore effect above: both
    // effects fire in the same commit once `contextLoaded`/`requestContext` update together, but
    // React runs passive effects using each render's own closure — so `personaSuggestions` here
    // would still read as its pre-restore value (null) even though the restore effect (declared
    // earlier) already called `setPersonaSuggestions` moments before in this same commit. Reading
    // `requestContext.personaSuggestions` directly sidesteps this, since `requestContext` itself
    // is already up to date in this render (it's set in the same tick as `contextLoaded`).
    const hasSavedPersonaSuggestions = Array.isArray(requestContext?.personaSuggestions)
        && requestContext.personaSuggestions.length > 0;
    useEffect(() => {
        if (autoSuggestedRef.current) return;
        if (personaOptions.length === 0) return;
        if (!editorView) return;
        if (!contextLoaded) return;
        if (hasSavedPersonaSuggestions) return;
        if (personaSuggestions !== null) return;
        autoSuggestedRef.current = true;
        handleSuggestPersonas();
        // handleSuggestPersonas is stable (useCallback); including it keeps the linter happy
        // while the ref guard prevents accidental re-runs if the identity ever changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personaOptions, editorView, contextLoaded, hasSavedPersonaSuggestions, personaSuggestions]);

    /** Toggle a persona checkbox on/off and remember the user's explicit choice. */
    const handleTogglePersona = useCallback((option) => {
        manuallyToggledRef.current.add(option.handle);
        setSelectedPersonas((prev) => {
            const isSelected = prev.some((p) => p.handle === option.handle);
            return isSelected
                ? prev.filter((p) => p.handle !== option.handle)
                : [...prev, option];
        });
    }, []);

    // Once suggestions exist, show the most suitable personas first; otherwise keep discovery order.
    const sortedPersonaOptions = useMemo(() => {
        if (!personaSuggestions) return personaOptions;
        return [...personaOptions].sort((a, b) => {
            const scoreA = personaSuggestions[a.handle]?.suitabilityScore ?? -1;
            const scoreB = personaSuggestions[b.handle]?.suitabilityScore ?? -1;
            return scoreB - scoreA;
        });
    }, [personaOptions, personaSuggestions]);

    return (
        <Stack flexGrow={1} overflow={'auto'}>
            <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="fullWidth"
                sx={{borderBottom: 1, borderColor: 'divider', flexShrink: 0}}
            >
                <Tab label="Generate" value="generate"/>
                <Tab label="History" value="history"/>
            </Tabs>

            {activeTab === 'history' ? (
                <ForecastHistory reportId={reportId} isOwner={isOwner} availablePersonas={availablePersonas}/>
            ) : (
                <>
            <Stack spacing={3} p={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <TrendingUp color="primary"/>
                    <Typography variant="body2" color="text.secondary">
                        Generate a forward-looking forecast based on your analysis.
                    </Typography>
                </Stack>

                <TextField
                    label="Add context (optional)"
                    placeholder="E.g., Focus on supply chain risks, assume no policy changes..."
                    multiline
                    rows={3}
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    fullWidth
                    disabled={isGenerating}
                />

                <FormControl fullWidth disabled={isGenerating}>
                    <InputLabel id="forecast-horizon-label">Horizon</InputLabel>
                    <Select
                        labelId="forecast-horizon-label"
                        label="Horizon"
                        value={horizon}
                        onChange={(e) => setHorizon(e.target.value)}
                    >
                        {HORIZON_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Stack spacing={0.5} sx={{px: 1, pb: 1}}>
                    <Typography variant="caption" color="text.secondary" sx={{fontWeight: 500}}>
                        Analysis Depth
                    </Typography>
                    <Slider
                        value={creativity}
                        onChange={(_, value) => setCreativity(value)}
                        step={1}
                        min={0}
                        max={3}
                        marks={CREATIVITY_MARKS}
                        disabled={isGenerating}
                        valueLabelDisplay="off"
                        sx={{
                            '& .MuiSlider-markLabel[data-index="0"]': {transform: 'translateX(0%)'},
                            '& .MuiSlider-markLabel[data-index="3"]': {transform: 'translateX(-100%)'},
                        }}
                    />
                </Stack>

                <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={600}>
                            Personas
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={suggestingPersonas
                                ? <CircularProgress size={14} color="inherit"/>
                                : <AutoAwesome fontSize="small"/>}
                            onClick={handleSuggestPersonas}
                            disabled={isGenerating || suggestingPersonas || personaOptions.length === 0}
                        >
                            {suggestingPersonas
                                ? 'Analyzing...'
                                : personaSuggestions
                                    ? 'Re-suggest'
                                    : 'Suggest personas'}
                        </Button>
                    </Stack>

                    {suggestError && (
                        <Alert severity="error" onClose={() => setSuggestError(null)}>
                            {suggestError}
                        </Alert>
                    )}

                    {personaOptions.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No personas available yet.
                        </Typography>
                    ) : (
                        <FormGroup>
                            {sortedPersonaOptions.map((option) => {
                                const suggestion = personaSuggestions?.[option.handle];
                                const isChecked = selectedPersonas.some((p) => p.handle === option.handle);
                                return (
                                    <FormControlLabel
                                        key={option.handle}
                                        disabled={isGenerating}
                                        sx={{alignItems: 'flex-start', ml: 0, my: 0.5}}
                                        control={
                                            <Checkbox
                                                checked={isChecked}
                                                onChange={() => handleTogglePersona(option)}
                                                sx={{pt: 0.25}}
                                            />
                                        }
                                        label={
                                            <Stack direction="row" spacing={1.25} sx={{pt: 0.75}}>
                                                <UserAvatar user={option} sx={{width: 28, height: 28, fontSize: 12}}/>
                                                <Stack spacing={0.25} flex={1}>
                                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                                        <Typography variant="body2">
                                                            {option.displayName || option.handle}
                                                        </Typography>
                                                        {roleLabel(option) && (
                                                            <Chip
                                                                label={roleLabel(option)}
                                                                size="small"
                                                                sx={{height: 16, fontSize: 10, '& .MuiChip-label': {px: 0.75}}}
                                                            />
                                                        )}
                                                        {suggestion?.suitabilityScore != null && (
                                                            <Chip
                                                                label={`${suggestion.suitabilityScore}% match`}
                                                                size="small"
                                                                color={suggestion.suitabilityScore >= 90 ? 'success' : 'primary'}
                                                                sx={{height: 16, fontSize: 10, '& .MuiChip-label': {px: 0.75}}}
                                                            />
                                                        )}
                                                    </Box>
                                                    {suggestion?.reason && (
                                                        <Typography
                                                            variant="caption"
                                                            color="primary.main"
                                                            sx={{fontStyle: 'italic'}}
                                                        >
                                                            {suggestion.reason}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        }
                                    />
                                );
                            })}
                        </FormGroup>
                    )}

                    <Alert severity="info">
                        Persona opinions are a synthetic projection based on analyst profiles.
                        Make sure to always confirm with the actual SME!
                    </Alert>
                </Stack>


                <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerate}
                        disabled={isGenerating || !editorView}
                        startIcon={isGenerating ? <CircularProgress size={20} color="inherit"/> : <TrendingUp/>}
                        sx={{flexGrow: 1}}
                    >
                        <span aria-live="polite">{generateButtonLabel}</span>
                    </Button>
                    {isGenerating && (
                        <Tooltip title="Cancel forecast generation">
                            <IconButton aria-label="Cancel forecast generation" onClick={handleCancel} color="error">
                                <CancelIcon/>
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>

                {generateStatusExplanation && (
                    <Typography variant="caption" color="text.secondary" sx={{fontStyle: 'italic'}}>
                        {generateStatusExplanation}
                    </Typography>
                )}

                {error && (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                )}
            </Stack>

            {/* Render generated forecast */}
            {forecast && (
                <Stack>
                    <Divider/>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" px={2} pt={2}>
                        <Typography variant="subtitle2" fontWeight="bold">
                            Forecast
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                            <Tooltip title={copiedKey === 'all' ? 'Copied!' : 'Copy text'}>
                                <IconButton
                                    aria-label="Copy text"
                                    size="small"
                                    onClick={() => handleCopyText(formatForecastAsText(forecast), 'all')}
                                    color={copiedKey === 'all' ? 'success' : 'primary'}
                                >
                                    <CopyIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Add to document">
                                <IconButton
                                    aria-label="Add to document"
                                    size="small"
                                    onClick={handleAddToDocument}
                                    color="primary"
                                    disabled={!editorView}
                                >
                                    <AddIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>

                    <Stack px={2} py={2} spacing={2}>
                        {forecast.summary && (
                            <ForecastPreviewText
                                text={forecast.summary}
                                sourcesByKey={sourcesByKey}
                                numberByKey={numberByKey}
                            />
                        )}

                        {forecast.confidence && (
                            <Chip
                                label={`Overall confidence: ${forecast.confidence}`}
                                size="small"
                                sx={{alignSelf: 'flex-start'}}
                            />
                        )}

                        {forecast.keyDrivers?.length > 0 && (
                            <Stack spacing={0.5}>
                                <Typography variant="subtitle2" color="primary">
                                    Key Drivers
                                </Typography>
                                {forecast.keyDrivers.map((driver, idx) => (
                                    <Stack key={idx} direction="row" spacing={0.5}>
                                        <Typography variant="body2">•</Typography>
                                        <ForecastPreviewText
                                            text={driver}
                                            sourcesByKey={sourcesByKey}
                                            numberByKey={numberByKey}
                                        />
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        {forecast.predictions?.length > 0 && (
                            <Stack spacing={2}>
                                <Typography variant="subtitle2" color="primary">
                                    Predictions
                                </Typography>
                                {forecast.predictions.map((pred, idx) => (
                                    <React.Fragment key={idx}>
                                    <Stack spacing={1} alignItems="flex-start">
                                        <ForecastPreviewText
                                            text={`${idx + 1}. ${pred.statement}`}
                                            citedSources={pred.citedSources}
                                            sourcesByKey={sourcesByKey}
                                            numberByKey={numberByKey}
                                            bold />
                                        
                                        {/* <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Chip
                                                label={pred.basis === 'data' ? 'Data-backed' : 'Assumption'}
                                                size="small"
                                                color={pred.basis === 'data' ? 'success' : 'default'}
                                                variant={pred.basis === 'data' ? 'filled' : 'outlined'} />
                                                
                                            <Typography variant="caption" color="text.secondary">
                                                Confidence: {pred.confidence}
                                            </Typography>
                                        </Stack> */}
                                        {pred.rationale && (
                                            <Stack spacing={0.25} sx={{width: '100%'}}>
                                                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                                    Rationale
                                                </Typography>
                                                <Typography variant="body2">
                                                    {pred.rationale}
                                                </Typography>
                                            </Stack>
                                        )}
                                        {pred.citedSources?.length > 0 && (
                                            <Stack spacing={0.25} sx={{width: '100%'}}>
                                                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                                                    Sources
                                                </Typography>
                                                <ForecastSourcesList
                                                    citedSources={pred.citedSources}
                                                    sourcesByKey={sourcesByKey}
                                                    numberByKey={numberByKey} />
                                            </Stack>
                                        )}
                                    </Stack>
                                    <Divider />
                                    </React.Fragment>
                                ))}
                            </Stack>
                        )}
                    </Stack>

                    {/* Expert opinions */}
                    {forecast.personaOpinions?.length > 0 && (
                        <Stack>
                            <Divider/>
                            <Stack px={2} pt={2} spacing={1}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    Expert Opinions
                                </Typography>
                                <Alert severity="info">
                                    Expert opinions are a synthetic projection based on analyst profiles and prior work —
                                    always confirm with the actual SME.
                                </Alert>
                            </Stack>
                            <Stack px={2} py={2} spacing={2}>
                                {forecast.personaOpinions.map((op, idx) => {
                                    const personaProfile = availablePersonas.find((p) => p.handle === op.handle);
                                    const hasPoints = (op.agreements?.length > 0) || (op.disagreements?.length > 0);
                                    const copyText = hasPoints
                                        ? [
                                            ...(op.agreements || []).map((pt) => `✓ ${pt.point}${pt.evidence ? ` (${pt.evidence})` : ''}`),
                                            ...(op.disagreements || []).map((pt) => `✗ ${pt.point}${pt.evidence ? ` (${pt.evidence})` : ''}`),
                                        ].join('\n')
                                        : (op.opinion || '');
                                    return (
                                        <Stack key={op.handle || idx} spacing={0.5}>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <UserAvatar
                                                        user={personaProfile || {handle: op.handle, displayName: op.displayName}}
                                                        sx={{width: 24, height: 24, fontSize: 12}}
                                                    />
                                                    <Typography variant="subtitle2">
                                                        {op.displayName || op.handle}
                                                    </Typography>
                                                    {op.confidence != null && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {typeof op.confidence === 'number'
                                                                ? `${Math.round(op.confidence * 100)}% confidence`
                                                                : `Confidence: ${op.confidence}`}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                <Tooltip title={copiedKey === op.handle ? 'Copied!' : 'Copy text'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCopyText(copyText, op.handle)}
                                                        color={copiedKey === op.handle ? 'success' : 'primary'}
                                                    >
                                                        <CopyIcon fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>

                                            {hasPoints ? (
                                                <Stack spacing={0.5} sx={{pl: 1}}>
                                                    {op.agreements?.map((pt, i) => (
                                                        <Stack key={i} spacing={0.25}>
                                                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                                                <CheckCircleOutline sx={{fontSize: 15, color: 'success.main', mt: '4px', flexShrink: 0}}/>
                                                                <ForecastPreviewText
                                                                    text={pt.point}
                                                                    sourcesByKey={sourcesByKey}
                                                                    numberByKey={numberByKey}
                                                                />
                                                            </Stack>
                                                            {pt.evidence && (
                                                                <OpinionEvidenceText evidence={pt.evidence}/>
                                                            )}
                                                        </Stack>
                                                    ))}
                                                    {op.disagreements?.map((pt, i) => (
                                                        <Stack key={i} spacing={0.25}>
                                                            <Stack direction="row" spacing={0.75} alignItems="flex-start">
                                                                <CancelOutlined sx={{fontSize: 15, color: 'warning.main', mt: '4px', flexShrink: 0}}/>
                                                                <ForecastPreviewText
                                                                    text={pt.point}
                                                                    sourcesByKey={sourcesByKey}
                                                                    numberByKey={numberByKey}
                                                                />
                                                            </Stack>
                                                            {pt.evidence && (
                                                                <OpinionEvidenceText evidence={pt.evidence}/>
                                                            )}
                                                        </Stack>
                                                    ))}
                                                    <PersonaReferencesList points={[...(op.agreements || []), ...(op.disagreements || [])]}/>
                                                </Stack>
                                            ) : op.opinion ? (
                                                <Typography variant="body2">{op.opinion}</Typography>
                                            ) : null}
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            )}
                </>
            )}
        </Stack>
    );
};

export default Forecast;
